import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CreateTicket,
  GetTicketFileSharedId,
  GetTicketsList,
  GetTicketTopics,
  RateTicket,
  ReserveTicketFile,
  RevokeTicket,
} from 'app/api/tickets';
import { GetFileFromFilesMS, SaveFileToFilesMS } from 'app/api/files';
import { showWarning } from 'app/store/root-slice';
import { RootState } from 'app/store/store';
import { IBlankTicket, ITicket, ITicketTopic } from './types';
import { IPhone } from 'app/types';
import { getTenantPhones } from '../profile/profile-slice';
import { AddTenantPhone, RemoveTenantPhone } from 'app/api/profile';
import { transformPhonesToBackend } from './utils';

export interface InitialState {
  statuses: {
    loaded: boolean;
    onLoad: boolean;
  };
  tickets: {
    selected: ITicket | null;
    list: Array<ITicket>;
    count: number;
    canCreate: boolean;
  };
  topics: {
    list: Array<ITicketTopic>;
    count: number;
  };
  filters: {
    limit: number;
    offset: number;
  };
  blankTicket: IBlankTicket;
  commonPhones: IPhone[];
}

export const initialState: InitialState = {
  statuses: {
    loaded: false,
    onLoad: false,
  },
  tickets: {
    selected: null,
    list: [],
    count: 0,
    canCreate: true,
  },
  topics: {
    list: [],
    count: 0,
  },
  filters: {
    limit: 10,
    offset: 0,
  },
  blankTicket: {
    topic: {
      _id: '',
      name: '',
    },
    description: '',
    phones: [],
    photos: [],
  },
  commonPhones: [],
};

export const getTicketsList = createAsyncThunk<any>(
  'tickets/getTicketsList',
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    try {
      const data = await GetTicketsList(state.tickets.filters);
      return {
        list: data.result || [],
        count: data.count,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getTopics = createAsyncThunk<any>(
  'tickets/getTopics',
  async (_, { rejectWithValue }) => {
    try {
      const data: Array<ITicketTopic> = await GetTicketTopics();

      return {
        list: data ?? [],
        count: data?.length ?? 0,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const revokeTicket = createAsyncThunk<ITicket, string, any>(
  'tickets/revokeTicket',
  async (ticketID, { rejectWithValue }) => {
    try {
      return await RevokeTicket(ticketID);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createTicket = createAsyncThunk<ITicket, IBlankTicket, any>(
  'tickets/createTicket',
  async (ticket, { rejectWithValue, dispatch }) => {
    try {
      return await CreateTicket({
        topic: {
          _id: ticket.topic._id || null,
          name: ticket.topic.name,
        },
        description: ticket.description,
        requester_files: ticket.requester_files,
        phone_numbers: transformPhonesToBackend(ticket.phones),
      });
    } catch (e) {
      dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const reserveTicketFile = createAsyncThunk<
  any,
  { filename: string },
  any
>('requests/reserveTicketFile', async ({ filename }, { rejectWithValue }) => {
  try {
    return await ReserveTicketFile({ filename });
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const uploadTicketFile = createAsyncThunk<
  any,
  { shared_id: string; file: File },
  any
>(
  'requests/uploadTicketFile',
  async ({ shared_id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('importance', 'low');

      return await SaveFileToFilesMS(shared_id, formData);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const downloadTicketFile = createAsyncThunk<
  any,
  { file_ids: string; filenames: string },
  any
>(
  'requests/downloadTicketFile',
  async ({ file_ids, filenames }, { rejectWithValue }) => {
    try {
      const ids = await GetTicketFileSharedId({ file_ids, filenames });
      return await GetFileFromFilesMS(Object.values(ids)[0]);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const addPhoneAsync = createAsyncThunk(
  'tickets/addPhoneAsync',
  async (phone: IPhone, { rejectWithValue }) => {
    try {
      return await AddTenantPhone(phone);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removePhoneAsync = createAsyncThunk(
  'tickets/removePhoneAsync',
  async (
    { phoneNumber, index }: { phoneNumber: string; index: number },
    { rejectWithValue }
  ) => {
    try {
      await RemoveTenantPhone(phoneNumber);

      return index;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const rateTicket = createAsyncThunk<any, any, any>(
  'tickets/rateTicket',
  async ({ ticketID, score }, { rejectWithValue }) => {
    try {
      return await RateTicket(ticketID, { score });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    clearErrors: (state) => {},
    forbidTicketCreation: (state) => {
      state.tickets.canCreate = false;
    },
    setBlankTicket: (state, action: PayloadAction<IBlankTicket | null>) => {
      state.blankTicket = action.payload ?? initialState.blankTicket;
    },
    selectTicket: (state, action: PayloadAction<ITicket | null>) => {
      state.tickets.selected = action.payload;
    },
    resetStore: (state) => ({
      ...initialState,
      blankTicket: {
        ...initialState.blankTicket,
        phones: state.commonPhones,
        photos: state.blankTicket.photos,
      },
      commonPhones: state.commonPhones,
    }),
  },
  extraReducers: (builder) => {
    // Получение списка тикетов
    builder.addCase(getTicketsList.pending, (state, action) => {
      state.statuses.loaded = true;
      state.statuses.onLoad = true;
    });

    builder.addCase(
      getTicketsList.fulfilled,
      (
        state,
        action: PayloadAction<{
          list: ITicket[];
          count: number;
        }>
      ) => {
        const { list, count } = action.payload;
        state.tickets.list = [...state.tickets.list, ...list];
        state.tickets.count = count;
        state.filters.offset += 10;
        state.statuses.onLoad = false;
      }
    );

    builder.addCase(getTenantPhones.fulfilled, (state, { payload }) => {
      state.blankTicket.phones = payload;
      state.commonPhones = payload;
    });

    builder
      .addCase(getTopics.pending, (state) => {
        state.statuses.loaded = true;
        state.statuses.onLoad = true;
      })
      .addCase(
        getTopics.fulfilled,
        (
          state,
          action: PayloadAction<{
            list: Array<ITicketTopic>;
          }>
        ) => {
          state.topics.list = action.payload.list;
        }
      );

    builder.addCase(revokeTicket.fulfilled, (state, action) => {
      const updatedTicket = action.payload;
      const updatedTicketIndex = state.tickets.list.findIndex(
        (el) => el._id === updatedTicket._id
      );

      if (updatedTicketIndex !== -1) {
        state.tickets.list[updatedTicketIndex] = updatedTicket;
        state.tickets.selected = updatedTicket;
      }
    });

    builder.addCase(addPhoneAsync.fulfilled, (state, action) => {
      state.blankTicket.phones = action.payload;
    });

    builder.addCase(removePhoneAsync.fulfilled, (state, action) => {
      state.blankTicket.phones.splice(action.payload, 1);
    });

    builder.addCase(createTicket.fulfilled, (state, action) => {
      state.tickets.list.unshift(action.payload);
    });

    builder.addCase(rateTicket.fulfilled, (state, action) => {
      state.tickets.selected = action.payload;
    });
  },
});

export const {
  selectTicket,
  setBlankTicket,
  resetStore,
  forbidTicketCreation,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
